import {StringOrTemplate} from '@frontapp/workflows/expressions';
import {isString} from 'lodash';
import {Edge} from 'reactflow';

import {ErrorWithData} from '../../../../libs/shared/core/src/helpers/types/customErrors';
import {
  DecisionTreeBranchNodeDataFrontChat,
  DecisionTreeNodeDataFrontChat,
  isDecisionTreeBranchNodeDataFrontChat,
  NodeDataFrontChatTypesEnum,
  ParseExpressionOperationsEnum,
} from '../../../../libs/shared/core/src/models/api/decisionTreeNodeDataModel';
import {validateTreeData} from '../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/helpers';
import {
  DecisionTreeEdge,
  DecisionTreeNode,
  TreeData,
} from '../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/types';
import {
  maxChatBotNodesCount,
  maxMessageContentLength,
  maxPromptContentLength,
  multiChoiceMinOptions,
  parseMinOptions,
  parseTextMaxKeywordSetSize,
} from './constants';
import {isParseExpressionOptionEmpty} from './expressions/expressionHelpers';
import {ChatBotBuilderNodeData, ChatBotBuilderValidation, ChatBotBuilderValidationErrorsEnum} from './types';

/*
 * Validators.
 */

/** Determine if the tree has no user-added nodes. */
export function checkIsTreeEmptyState(edges: ReadonlyArray<Edge>): boolean {
  // Edges cannot go beneath 1, as root must be defined. This check is validated separately.
  return edges.length <= 1;
}

/**
 * Validates that the given tree data meets the conditions of our base decision tree, and then performs additional
 * checks specific to trees which represent a chatbot.
 */
export function validateChatBotBuilderTreeData(treeData: TreeData<DecisionTreeNodeDataFrontChat>) {
  const rootNode = validateTreeData(treeData);

  // The UI will show an empty state, but if inactive the user can still save.
  if (treeData.nodes.length < 2 && treeData.edges.length < 1) {
    throw new Error('Must have a minimum of 2 nodes and 1 edge for a Chatbot Builder');
  }

  // Cannot have more than the allowed maximum number of nodes. This may eventually vary by plan.
  if (treeData.nodes.length > maxChatBotNodesCount) {
    throw new Error(`Cannot have more than ${maxChatBotNodesCount} nodes in a bot`);
  }

  if (rootNode.data.type !== NodeDataFrontChatTypesEnum.START) {
    throw new ErrorWithData('Root chatbot node must have data of type "start"', {
      rootNode: {id: rootNode.id, type: rootNode.type},
    });
  }

  // To find all leaf nodes we need to first find the targets of every edge.
  const targetNodes = treeData.edges.map((edge) => treeData.nodes.find((node) => node.id === edge.target));
  for (const targetNode of targetNodes) {
    if (!targetNode) {
      throw new Error('Node could not be found for an edge target');
    }
  }

  // Leaf nodes are targeted nodes which have no edge referring to them as a source.
  const leafNodes = targetNodes.filter((node) => !treeData.edges.find((edge) => edge.source === node?.id));
  for (const leafNode of leafNodes) {
    if (leafNode?.data.type !== NodeDataFrontChatTypesEnum.END) {
      throw new Error('Leaf node found with a type other than "end"');
    }
  }

  const branchNodes = treeData.nodes.filter(
    (node): node is DecisionTreeNode<DecisionTreeBranchNodeDataFrontChat> =>
      isDecisionTreeBranchNodeDataFrontChat(node.data),
  );
  for (const node of branchNodes) {
    validateChatBotBuilderBranchNode(treeData.edges, node);
  }
}

/**
 * Ensures that a branch node has options that are valid, meaning that each option has a sourceHandle that
 * corresponds to a matching edge. Also ensures that the last option is empty for the PARSE_TEXT node type.
 */
function validateChatBotBuilderBranchNode(
  edges: ReadonlyArray<Edge>,
  node: DecisionTreeNode<DecisionTreeBranchNodeDataFrontChat>,
) {
  const sourceHandleEdgeMap: Record<string, DecisionTreeEdge> = Object.fromEntries(
    edges.map((edge) => [edge.sourceHandle, edge]),
  );

  for (const option of node.data.options) {
    const matchingEdge = sourceHandleEdgeMap[option.sourceHandle];
    if (!matchingEdge) {
      throw new ErrorWithData('sourceHandle for option has no matching edge', {
        sourceHandle: option.sourceHandle,
      });
    }

    if (matchingEdge.source !== node.id) {
      throw new ErrorWithData(
        'source of an edge should match the id of the node whose options include the same sourceHandle as that edge',
        {edgeSource: matchingEdge.source, nodeId: node.id},
      );
    }
  }

  if (node.data.type === NodeDataFrontChatTypesEnum.PARSE_TEXT) {
    if (node.data.options.length === 0 || node.data.options[node.data.options.length - 1].value.length > 0) {
      throw new ErrorWithData('The last option in a PARSE_TEXT node should always exist and be empty', {
        nodeId: node.id,
        options: node.data.options,
      });
    }
  }

  if (node.data.type === NodeDataFrontChatTypesEnum.SEMANTIC_SEARCH) {
    if (node.data.options.length !== 2) {
      throw new ErrorWithData('There should always be exactly 2 options for a SEMANTIC_SEARCH node', {
        nodeId: node.id,
        options: node.data.options,
      });
    }
  }
}

/**
 * Validator that can apply different tests depending on the type of node data being checked.
 */
export function validateNodeData(newData: ChatBotBuilderNodeData): ChatBotBuilderValidation {
  const validationErrors: ChatBotBuilderValidationErrorsEnum[] = [];

  switch (newData.type) {
    case NodeDataFrontChatTypesEnum.MESSAGE: {
      const messageLength = computeApproximateMessageLength(newData.message);

      if (messageLength === 0) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.EMPTY_MESSAGE);
      }

      if (messageLength > maxMessageContentLength) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.MESSAGE_TOO_LONG);
      }
      break;
    }
    case NodeDataFrontChatTypesEnum.PROMPT: {
      const messageLength = computeApproximateMessageLength(newData.message);

      if (messageLength === 0) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.EMPTY_MESSAGE);
      }

      if (messageLength > maxPromptContentLength) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.MESSAGE_TOO_LONG);
      }
      break;
    }
    case NodeDataFrontChatTypesEnum.MULTI_CHOICE: {
      const messageLength = computeApproximateMessageLength(newData.message);

      if (messageLength === 0) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.EMPTY_MESSAGE);
      }

      if (messageLength > maxPromptContentLength) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.MESSAGE_TOO_LONG);
      }

      if (newData.options.filter((option) => option.value.length > 0).length < multiChoiceMinOptions) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.MISSING_OPTIONS);
      }
      break;
    }
    case NodeDataFrontChatTypesEnum.PARSE_TEXT: {
      if (newData.options.filter((option) => option.value.length > 0).length < parseMinOptions) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.MISSING_OPTIONS);
      }
      if (newData.options.filter((option) => option.value.length > parseTextMaxKeywordSetSize).length > 0) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.TOO_MANY_KEYWORDS);
      }
      break;
    }
    case NodeDataFrontChatTypesEnum.TAG: {
      if (newData.tagIds.length === 0) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.EMPTY_TAGS);
      }
      break;
    }
    case NodeDataFrontChatTypesEnum.PARSE_EXPRESSION: {
      if (
        newData.options.filter(
          (option) =>
            !isParseExpressionOptionEmpty(option) && option.operation !== ParseExpressionOperationsEnum.ELSE,
        ).length < parseMinOptions
      ) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.MISSING_OPTIONS);
      }
      break;
    }
    case NodeDataFrontChatTypesEnum.CUSTOM_FIELDS:
    case NodeDataFrontChatTypesEnum.END:
    case NodeDataFrontChatTypesEnum.START:
    default:
      // These cases do not need validation.
      break;
  }

  return {
    isValid: validationErrors.length === 0,
    validationErrors,
  };
}

/**
 * Computes a validation result that can be used in the bot builder UI to determine if any
 * tooltips or error messages should be displayed.
 */
export function computeValidationResultHasError(
  validationResult: ChatBotBuilderValidation,
  errorType: ChatBotBuilderValidationErrorsEnum,
) {
  return validationResult.validationErrors.includes(errorType);
}

export function checkIsSubtreeSaveable(
  nodes: Array<DecisionTreeNode<DecisionTreeNodeDataFrontChat>>,
  edges: Array<Edge>,
) {
  return !checkIsTreeEmptyState(edges) && nodes.length < maxChatBotNodesCount;
}

/*
 * Helpers.
 */

/** Computes the length of a message, accounting for messages that are templates. */
function computeApproximateMessageLength(message: StringOrTemplate) {
  return isString(message)
    ? message.length
    : message.template.reduce((totalLength, part) => totalLength + (isString(part) ? part.length : 0), 0);
}
